import { hasValue, Vehicle } from "@alba-cars/common-modules";

/**
 * Truncates text to a maximum length
 */
export const truncateText = (text: string, maxLength: number = 100): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
};

/**
 * Returns the first option from an array or the value if it's not an array
 */
export const getValueOrFirstOption = <T>(value: T | T[]): T => {
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
};

/**
 * Checks if an object has any defined values (not null or undefined or empty)
 */
export const hasDefinedValues = (obj?: Record<string, unknown>): boolean => {
  const cleanedObject = obj ? Object.fromEntries(Object.entries(obj).filter(([_, value]) => hasValue(value))) : {};
  return (
    hasValue(cleanedObject) &&
    Object.keys(cleanedObject).length > 0 &&
    Object.values(cleanedObject).some((value) => hasValue(value))
  );
};

export const getS3Url = (fileName: string): string => {
  return `${process.env.NEXT_PUBLIC_AWS_S3_URL}/${fileName}`;
};

export const getCarNameTitle = (
  vehicle: Vehicle,
  { showTrim = false, showYear = false }: { showTrim?: boolean; showYear?: boolean },
): string => {
  if (vehicle.title) {
    return vehicle.title;
  } else {
    let result = `${vehicle.make.name} ${vehicle.model.name}`;

    if (showTrim && vehicle.trim) {
      result += ` ${vehicle.trim}`;
    }

    if (showYear && vehicle.year) {
      result += ` ${vehicle.year}`;
    }

    return result.trim();
  }
};

export function generateUniqueId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  } else {
    // Fallback method: using crypto.getRandomValues if available
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      const array = new Uint8Array(16);
      crypto.getRandomValues(array);
      array[6] = (array[6] & 0x0f) | 0x40;
      array[8] = (array[8] & 0x3f) | 0x80;
      return [...array]
        .map((b, i) => {
          const hex = b.toString(16).padStart(2, "0");
          return [4, 6, 8, 10].includes(i) ? `-${hex}` : hex;
        })
        .join("");
    } else {
      // Fallback to Math.random() if crypto is not available
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    }
  }
}
